import React, { useState } from 'react';
import axios from 'axios';
import SplitLoginLayout, {
  Form,
  Field,
  Label,
  Input,
  PasswordWrap,
  PasswordInput,
  EyeButton,
  EyeOpenIcon,
  EyeOffIcon,
  OptionsRow,
  Remember,
  SubmitButton,
  ErrorText,
} from '@medical/shared/components/SplitLoginLayout';
import HospitalLogo from '@medical/shared/components/HospitalLogo';
import { useHospitalBranding } from '@medical/shared/utils/hospitalBranding';

const DoctorLogin = ({ onLoginSuccess }) => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const branding = useHospitalBranding(identifier);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post('/api/auth/doctor/login', {
        identifier: identifier.trim(),
        password,
        rememberMe
      });
      const { token, refreshToken, user } = response.data;
      localStorage.setItem('doctorToken', token);
      if (refreshToken) {
        localStorage.setItem('doctorRefreshToken', refreshToken);
      }
      localStorage.setItem('doctorUser', JSON.stringify(user));
      onLoginSuccess(user, token);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not sign in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SplitLoginLayout
      title="Doctor Login"
      brandExtra={<HospitalLogo logoUrl={branding?.logoUrl} name={branding?.name} />}
      subtitle="Review your patients' pain logs and prescriptions."
      badgeLabel="DOCTOR"
      artSrc="/login-doctor.jpg"
      artAlt="Doctor reviewing a pain map with a patient"
      artCaption="See where it hurts, how it changes, and act sooner."
    >
      <Form onSubmit={handleSubmit}>
        <Field>
          <Label htmlFor="doctor-identifier">Doctor ID / Email</Label>
          <Input
            id="doctor-identifier"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder="Enter Doctor ID or email"
            required
            autoFocus
            autoComplete="username"
          />
        </Field>
        <Field>
          <Label htmlFor="doctor-password">Password</Label>
          <PasswordWrap>
            <PasswordInput
              id="doctor-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              autoComplete="current-password"
            />
            <EyeButton
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOffIcon /> : <EyeOpenIcon />}
            </EyeButton>
          </PasswordWrap>
        </Field>
        <OptionsRow>
          <Remember htmlFor="doctor-remember">
            <input id="doctor-remember" type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
            Remember me
          </Remember>
        </OptionsRow>
        {error && <ErrorText>{error}</ErrorText>}
        <SubmitButton type="submit" disabled={loading}>
          {loading ? 'Signing in...' : 'Sign in'}
        </SubmitButton>
      </Form>
    </SplitLoginLayout>
  );
};

export default DoctorLogin;
